<template>
  <transition name="zm-zoom-in-top" @after-enter="handleEnter" @after-leave="handleLeave">
    <div
      v-show="visible"
      class="zm-time-panel zm-popper"
      role="application"
      aria-label="Calendar view time-picker"
      :class="popperClass">
      <div class="zm-time-panel__content" :class="{ 'has-seconds': showSeconds, 'is-arrow': useArrow }">
        <time-spinner
          ref="spinner"
          @change="handleChange"
          :arrow-control="useArrow"
          :show-seconds="showSeconds"
          :am-pm-mode="amPmMode"
          @select-range="setSelectionRange"
          :date="date">
        </time-spinner>
      </div>
      <div class="zm-time-panel__footer">
        <button
          type="button"
          class="zm-time-panel__btn cancel"
          @click="handleCancel">{{ t('datepicker.cancel') }}</button>
        <button
          type="button"
          class="zm-time-panel__btn btn-firstFocus"
          :class="{confirm: !disabled}"
          @click="handleConfirm()">{{ t('datepicker.confirm') }}</button>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
  import { limitTimeRange, isDate, clearMilliseconds, timeWithinRange } from '../util';
  import Locale from '../../../mixins/locale';
  import TimeSpinner from '../basic/time-spinner';
  import AriaDialog from '../../../utils/aria-dialog';
  export default {
    mixins: [Locale],

    components: {
      TimeSpinner
    },

    props: {
      visible: Boolean,
      timeArrowControl: {
        type: Boolean,
        default: true
      }
    },

    watch: {
      visible(val) {
        if (val) {
          this.oldValue = this.value;
          this.$nextTick(() => this.$refs.spinner.emitSelectRange('hours'));
        } else {
          this.needInitAdjust = true;
        }
      },

      value(newVal) {
        let date;
        if (newVal instanceof Date) {
          date = limitTimeRange(newVal, this.selectableRange, this.format);
        } else if (!newVal) {
          date = this.defaultValue ? new Date(this.defaultValue) : new Date();
        }

        this.date = date;
        if (this.visible && this.needInitAdjust) {
          this.$nextTick(_ => this.adjustSpinners());
          this.needInitAdjust = false;
        }
      },

      selectableRange(val) {
        this.$refs.spinner.selectableRange = val;
      },

      defaultValue(val) {
        if (!isDate(this.value)) {
          this.date = val ? new Date(val) : new Date();
        }
      }
    },

    data() {
      return {
        popperClass: '',
        format: 'HH:mm:ss',
        value: '',
        defaultValue: null,
        date: new Date(),
        oldValue: new Date(),
        selectableRange: [],
        selectionRange: [0, 2],
        disabled: false,
        arrowControl: false,
        needInitAdjust: true
      };
    },

    computed: {
      showSeconds() {
        return (this.format || '').indexOf('ss') !== -1;
      },
      useArrow() {
        return this.arrowControl || this.timeArrowControl || false;
      },
      amPmMode() {
        if ((this.format || '').indexOf('A') !== -1) return 'A';
        if ((this.format || '').indexOf('a') !== -1) return 'a';
        return '';
      }
    },

    methods: {
      handleCancel() {
        this.$emit('pick', this.oldValue, false);
      },

      handleChange(date) {
        // this.visible avoids edge cases, when use scrolls during panel closing animation
        if (this.visible) {
          this.date = clearMilliseconds(date);
          // if date is out of range, do not emit
          if (this.isValidValue(this.date)) {
            this.$emit('pick', this.date, true);
          }
        }
      },

      setSelectionRange(start, end) {
        this.$emit('select-range', start, end);
        this.selectionRange = [start, end];
      },

      handleConfirm(visible = false, first) {
        if (first) return;
        const date = clearMilliseconds(limitTimeRange(this.date, this.selectableRange, this.format));
        this.$emit('pick', date, visible, first);
      },

      handleKeydown(event) {
        const keyCode = event.keyCode;
        const mapping = { 38: -1, 40: 1, 37: -1, 39: 1 };

        // Left or Right
        if (keyCode === 37 || keyCode === 39) {
          const step = mapping[keyCode];
          this.changeSelectionRange(step);
          event.preventDefault();
          return;
        }

        // Up or Down
        if (keyCode === 38 || keyCode === 40) {
          const step = mapping[keyCode];
          this.$refs.spinner.scrollDown(step);
          event.preventDefault();
          return;
        }
      },

      keyEventHandler(event) {
        if (event.keyCode === 27) {
          this.$emit('escClose');
          this.handleLeave();
          // this.visible = false;
        }
      },

      handleEnter() {
        document.body.addEventListener('keydown', this.keyEventHandler);
        const focusAfterClosed = null;
        this.$nextTick(() => {
          if (!this.ariaDialog) {
            let firstFocusEl = this.$el.querySelector('.btn-firstFocus');
            this.ariaDialog = new AriaDialog(this.$el, focusAfterClosed, firstFocusEl);
          }
        });

      },

      handleLeave() {
        this.$emit('dodestroy');
        this.flagLeave = true;
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
        document.body.removeEventListener('keydown', this.keyEventHandler);
      },

      isValidValue(date) {
        return timeWithinRange(date, this.selectableRange, this.format);
      },

      adjustSpinners() {
        return this.$refs.spinner.adjustSpinners();
      },

      changeSelectionRange(step) {
        const list = [0, 3].concat(this.showSeconds ? [6] : []);
        const mapping = ['hours', 'minutes'].concat(this.showSeconds ? ['seconds'] : []);
        const index = list.indexOf(this.selectionRange[0]);
        const next = (index + step + list.length) % list.length;
        this.$refs.spinner.emitSelectRange(mapping[next]);
      }
    },

    mounted() {
      this.$nextTick(() => this.handleConfirm(true, true));
      this.$emit('mounted');
    }
  };
</script>

<style lang="scss">
  @import "../../../scss/common/var";
  @import "../../../scss/mixins/mixins";

  @include b(time-panel) {
    margin: 5px 0;
    background-color: $--color-white;
    box-shadow: $--box-shadow-light;
    border-radius: $--border-radius-base;
    position: absolute;
    width: 180px;
    left: 0;
    z-index: $--index-top;

    @include e(content) {
      font-size: 0;
      position: relative;
      overflow: hidden;

      &:not(.is-arrow)::after, &:not(.is-arrow)::before {
        content: "";
        top: 50%;
        position: absolute;
        margin-top: -15px;
        height: 32px;
        z-index: -1;
        left: 0;
        right: 0;
        box-sizing: border-box;
        padding-top: 6px;
        text-align: left;
        border-top: 1px solid $--border-color-light;
        border-bottom: 1px solid $--border-color-light;
      }

      &::after {
        left: 50%;
        margin-left: 12%;
        margin-right: 12%;
      }

      &::before {
        padding-left: 50%;
        margin-right: 12%;
        margin-left: 12%;
      }

      &.has-seconds {
        &::after {
          left: calc(100% / 3 * 2);
        }

        &::before {
          padding-left: calc(100% / 3);
        }
      }
    }

    @include e(footer) {
      border-top: 1px solid $--datepicker-inner-border-color;
      padding: 4px;
      height: 36px;
      line-height: 25px;
      text-align: right;
      box-sizing: border-box;
    }

    @include e(btn) {
      border: none;
      line-height: 24px;
      padding: 0 5px;
      margin: 0 5px;
      cursor: pointer;
      background-color: transparent;
      outline: none;
      font-size: 12px;
      color: $--color-text-primary;
      &:hover,
      &:focus {
        color: $--color-primary;
      }
      &.confirm {
        font-weight: 800;
        color: $--datepicker-active-color;
        border-radius: $--button-small-border-radius;
        &:hover,
        &:focus {
          background-color: $--color-background-secondary-hover;
        }
      }
    }
  }
</style>
